import React, { useState, useEffect } from 'react';
import './FormModal.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api';
import Swal from 'sweetalert2';

const FormModalBulk = ({ onClose }) => {
  const [loading, setLoading] = useState(false); // ✅ Added loading state
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    const header = document.querySelector('.dashboard-header');

    if (window.innerWidth <= 768 && header) {
      header.style.display = 'none';
    }

    return () => {
      if (header) header.style.display = '';
    };
  }, []);


  // Trigger CSV upload input
  const handleBulkUpload = () => {
    document.getElementById('csv-upload')?.click();
    toast.info("Select a CSV file to bulk upload questions.");
  };

  // Upload CSV
  // Handle CSV file upload
  // CSV Upload
  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();
    const allowedExtensions = ['csv', 'xls', 'xlsx']; // ✅ Allow CSV and Excel

    if (!allowedExtensions.includes(fileExtension)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid File Type',
        text: 'Only CSV or Excel files are allowed. Please select a valid file.',
        confirmButtonColor: '#d33',
      });
      e.target.value = ''; // Reset input
      return;
    }

    const confirmResult = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to upload the file: ${file.name}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, upload it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
    });

    if (!confirmResult.isConfirmed) return;

    Swal.fire({
      title: 'Uploading...',
      text: 'Please wait while we upload and process the file.',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      const result = await api.post('/questions/bulk-upload', formData);

      Swal.fire({
        icon: 'success',
        title: 'Upload Successful!',
        text: result.data.message || 'Questions have been uploaded successfully.',
        confirmButtonColor: '#3085d6',
      });

      setErrors([]);
      e.target.value = ''; // Reset input
    } catch (error) {
      console.error('Upload error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: error.response?.data?.message || 'Something went wrong. Please check your file format.',
        confirmButtonColor: '#d33',
      });

      if (error.response?.data?.errors) setErrors(error.response.data.errors);
    } finally {
      setLoading(false);
    }
  };


  // Export existing questions as CSV
  // Export CSV
  const handleExportCSV = async () => {
    Swal.fire({
      title: 'Exporting CSV...',
      text: 'Please wait while we generate your file.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const response = await api.get('/questions/export', { responseType: 'blob' });
      // ✅ Changed: removed `response.ok` (Axios does not have it)

      const blob = response.data;
      // ✅ Changed: use `response.data` directly, already a blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'questions.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        icon: 'success',
        title: 'Export Complete',
        text: 'Your CSV file has been downloaded.',
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: error.response?.data?.message || 'Unable to export questions. Try again later.',
      });
    }
  };


  const handleDownloadTemplate = () => {
    const url = '/sample_template.csv';
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'sample_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Show success alert
    Swal.fire({
      icon: 'success',
      title: 'Download Started...',
      text: 'Your CSV template is downloading!',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const handleDownloadExcelTemplate = () => {
    const url = '/question_template.xlsx';
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'question_template.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Show success alert
    Swal.fire({
      icon: 'success',
      title: 'Download Started...',
      text: 'Your Excel template is downloading!',
      timer: 2000,
      showConfirmButton: false
    });
  };


  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close-icon" onClick={onClose}>&times;</button>

        <h2 className='bulk-upload-heading'>⬆️ Bulk Upload Questions</h2>

        {/* ⚠️ Important Note */}
        <p className="admin-note">
          ⚠️ <strong>Note:</strong> You can download the templates below in <strong>CSV or Excel</strong> format
          and enter your questions and options in plain text. <br />
          Only <strong>text-based questions and answers</strong> are supported in bulk upload.
          Do not include images or formatting. Make sure all required fields are filled.
        </p>

        {/* 📄 CSV Template Download */}
        <div className="template-links">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleDownloadTemplate();
            }}
            className="template-link"
          >
            ⬇️ Download CSV Template
          </a>

          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleDownloadExcelTemplate();
            }}
            className="template-link"
          >
            ⬇️ Download Excel Template
          </a>
        </div>






        {/* 📁 Upload CSV
        <input
          type="file"
          accept=".csv"
          onChange={handleCsvUpload}
          className="image-upload-input"
        />
        {loading && <div className="spinner"></div>} */}

        <div className="question-tools">
          {/* 📤 Trigger file input for CSV upload */}
          <button
            type="button"
            onClick={handleBulkUpload}
            className="secondary-btn"
            title="Upload multiple questions from a CSV file"
          >
            🧾Choose a File For Bulk Upload
          </button>

          {/* 📥 Trigger CSV export */}
          {/* <button
            type="button"
            onClick={handleExportCSV}
            className="secondary-btn"
            title="Download existing questions as CSV"
          >
            📥 Export CSV
          </button> */}

          {/* Hidden input to trigger on button click */}
          <input
            id="csv-upload"
            type="file"
            accept=".csv, .xls, .xlsx" // ✅ Allow CSV and Excel files
            onChange={handleCsvUpload}
            style={{ display: 'none' }}
          />
        </div>



        {/* ❗ Errors if any */}
        {errors.length > 0 && (
          <div className="error">
            <strong>Errors:</strong>
            <ul>
              {errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {/* ✅ Buttons */}
        <div className="modal-buttons">
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>

      {/* ✅ Toast container */}
      <ToastContainer
        position="bottom-right"
        autoClose={2500}
        theme="colored"
      />

    </div>


  );
};

export default FormModalBulk;


